import type {
  SurveySection,
  SurveyDraftSection,
} from "../../../../../shared/survey-questions";
import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { baseFastApiMatterRoute } from "@/lib/data/requests/matters/matter";
import {
  toSurveyQuestionInputs,
  toSurveySectionInputs,
  type MatterSurveyQuestionDefinition,
} from "@/lib/data/requests/matters/matter-survey";
import type { APIResponse } from "@/lib/types/APIResponse";

export interface MassTortSurvey {
  sections?: SurveySection[];
  id: string;
  firmId: string;
  massTortId: string;
  title: string;
  description: string | null;
  expirationDays: number;
  dateCreated: string;
  dateModified: string;
}

export interface MassTortSurveyQuestion extends MatterSurveyQuestionDefinition {
  id: string;
  massTortSurveyId: string;
  position: number;
  dateCreated: string;
  dateModified: string;
}

export interface MassTortSurveyDistribution {
  id: string;
  sourceMassTortSurveyId: string;
  sourceVersion: string;
  requestKey: string;
  acceptedAt: string;
  state: "accepted" | "processing" | "completed" | "completed_with_errors";
  counts: {
    total: number;
    pending: number;
    processing: number;
    created: number;
    skipped: number;
    failed: number;
  };
}

export interface MassTortSurveyDistributionItem {
  id: string;
  matterId: string;
  state: "pending" | "processing" | "created" | "skipped" | "failed";
  safeReasonCode: string | null;
  matterSurveyId: string | null;
  matterSurveyLink: string | null;
}

export interface CreateMassTortSurveyInput {
  sections?: SurveyDraftSection[];
  questions: (MatterSurveyQuestionDefinition & { key?: string; id?: string })[];
  massTortId: string;
  title: string;
  description?: string | null;
  expirationDays: number;
}

export interface UpdateMassTortSurveyInput {
  sections?: SurveyDraftSection[];
  questions: (MatterSurveyQuestionDefinition & { key?: string; id?: string })[];
  surveyId: string;
  dateModified: string;
  title: string;
  description: string | null;
  expirationDays: number;
}

export interface CreateMassTortSurveyQuestionInput {
  surveyId: string;
  question: MatterSurveyQuestionDefinition;
}

export interface UpdateMassTortSurveyQuestionInput {
  surveyId: string;
  questionId: string;
  dateModified: string;
  question: MatterSurveyQuestionDefinition;
}

export interface ReorderMassTortSurveyQuestionsInput {
  surveyId: string;
  dateModified: string;
  questionIds: string[];
}

export interface CreateMassTortSurveyDistributionInput {
  surveyId: string;
  dateModified: string;
  requestKey: string;
  selectionMode: "all" | "subset";
  matterIds?: string[];
}

const surveyRoute = `${baseFastApiMatterRoute}/mass-tort-surveys`;
const conditionalSurveyRoute = surveyRoute.replace("/v2/", "/v4/");
const distributionRoute = `${baseFastApiMatterRoute}/mass-tort-survey-distributions`;

export const listMassTortSurveys = (massTortId: string, pageNumber = 1) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<MassTortSurvey[]>>(surveyRoute, {
      params: { massTortId, pageNumber, pageSize: 50 },
    })
    .then((response) => response.data);

export const createMassTortSurvey = (input: CreateMassTortSurveyInput) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MassTortSurvey>
    >(conditionalSurveyRoute, { ...input, sections: toSurveySectionInputs(input.sections ?? []), questions: toSurveyQuestionInputs(input.questions) })
    .then((response) => response.data);

export const retrieveMassTortSurvey = (surveyId: string) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MassTortSurvey & { questions: MassTortSurveyQuestion[] }>
    >(`${conditionalSurveyRoute}/${surveyId}`)
    .then((response) => response.data);

export const updateMassTortSurvey = ({
  surveyId,
  ...input
}: UpdateMassTortSurveyInput) =>
  authenticatedFastApiAxiosInstance
    .put<
      APIResponse<MassTortSurvey>
    >(`${conditionalSurveyRoute}/${surveyId}`, { ...input, sections: toSurveySectionInputs(input.sections ?? [], true), questions: toSurveyQuestionInputs(input.questions, true) })
    .then((response) => response.data);

export const deleteMassTortSurvey = (surveyId: string) =>
  authenticatedFastApiAxiosInstance
    .delete<APIResponse<MassTortSurvey>>(`${surveyRoute}/${surveyId}`)
    .then((response) => response.data);

export const listMassTortSurveyQuestions = (surveyId: string) =>
  retrieveMassTortSurvey(surveyId).then((response) => ({
    ...response,
    data: response.data.questions,
    sections: response.data.sections ?? [],
  }));

export const createMassTortSurveyQuestion = ({
  surveyId,
  question,
}: CreateMassTortSurveyQuestionInput) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MassTortSurveyQuestion>
    >(`${surveyRoute}/${surveyId}/questions`, question)
    .then((response) => response.data);

export const updateMassTortSurveyQuestion = ({
  surveyId,
  questionId,
  dateModified,
  question,
}: UpdateMassTortSurveyQuestionInput) =>
  authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<MassTortSurveyQuestion>
    >(`${surveyRoute}/${surveyId}/questions/${questionId}`, { dateModified, ...question })
    .then((response) => response.data);

export const deleteMassTortSurveyQuestion = ({
  surveyId,
  questionId,
}: {
  surveyId: string;
  questionId: string;
}) =>
  authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<MassTortSurveyQuestion>
    >(`${surveyRoute}/${surveyId}/questions/${questionId}`)
    .then((response) => response.data);

export const reorderMassTortSurveyQuestions = ({
  surveyId,
  ...input
}: ReorderMassTortSurveyQuestionsInput) =>
  authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<MassTortSurveyQuestion[]>
    >(`${surveyRoute}/${surveyId}/questions/order`, input)
    .then((response) => response.data);

export const createMassTortSurveyDistribution = ({
  surveyId,
  ...input
}: CreateMassTortSurveyDistributionInput) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MassTortSurveyDistribution>
    >(`${conditionalSurveyRoute}/${surveyId}/distributions`, input)
    .then((response) => response.data);

export const retrieveMassTortSurveyDistribution = (requestId: string) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MassTortSurveyDistribution>
    >(`${distributionRoute}/${requestId}`)
    .then((response) => response.data);

export const listMassTortSurveyDistributionItems = (
  requestId: string,
  pageNumber: number,
) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MassTortSurveyDistributionItem[]>
    >(`${distributionRoute}/${requestId}/items`, { params: { pageNumber, pageSize: 50 } })
    .then((response) => response.data);
